import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    Activity,
    ArrowDownToDot,
    Box,
    Boxes,
    Check,
    Clock,
    Container,
    Copy,
    FileText,
    HardDrive,
    LogOut,
    Menu,
    Plus,
    Send,
    Settings as SettingsIcon,
    ShieldCheck,
    Sparkles,
    Terminal,
    Trash2,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { StatusDot } from "@/components/mdh/primitives";
import { useAuth } from "@/auth/AuthContext";
import LiveStream from "@/components/dashboard/LiveStream";
import ChildrenGrid from "@/components/dashboard/ChildrenGrid";
import UptimePanel from "@/components/dashboard/UptimePanel";
import ReceiverPanel from "@/components/dashboard/ReceiverPanel";
import KeysPanel from "@/components/dashboard/KeysPanel";
import NodePanel from "@/components/dashboard/NodePanel";
import OutboundPanel from "@/components/dashboard/OutboundPanel";
import DevelopPanel from "@/components/dashboard/DevelopPanel";
import CurlPanel from "@/components/dashboard/CurlPanel";
import ConsolePanel from "@/components/dashboard/ConsolePanel";
import FlowVisualizer from "@/components/dashboard/FlowVisualizer";
import ApiReference from "@/components/dashboard/ApiReference";
import CronPanel from "@/components/dashboard/CronPanel";
import SslPanel from "@/components/dashboard/SslPanel";
import { useAppSocket } from "@/components/dashboard/useAppSocket";
import { API, BACKEND } from "@/lib/api";


const StatCard = ({ label, value, sub, testid }) => (
    <div
        data-testid={testid}
        className="rounded-xl border border-slate-800/80 bg-[#111827] p-5"
    >
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            {label}
        </div>
        <div className="font-display mt-2 text-2xl font-bold tracking-tight text-slate-100">
            {value}
        </div>
        {sub && <div className="mt-1 font-mono text-[10px] text-slate-500">{sub}</div>}
    </div>
);

const TEMPLATE_ICONS = {
    "webhook-receiver": ArrowDownToDot,
    "outbound-webhooks": Send,
    "rest-api-monitor": Activity,
    "cron-monitor": Clock,
    "ssl-cert-monitor": ShieldCheck,
    "node-project-monitor": Box,
    "local-machine-monitor": HardDrive,
    "docker-monitor": Container,
    "log-watcher": FileText,
};

// tiny glowing sparkline — last hour of events in 12 five-minute buckets
const Sparkline = ({ data }) => {
    if (!data || !data.some((n) => n > 0)) {
        return (
            <div className="mt-3 flex h-6 items-center">
                <span className="font-mono text-[9px] text-slate-600">quiet last hour</span>
            </div>
        );
    }
    const max = Math.max(...data, 1);
    const pts = data
        .map((n, i) => `${((i / (data.length - 1)) * 100).toFixed(1)},${(21 - (n / max) * 18).toFixed(1)}`)
        .join(" ");
    return (
        <svg
            data-testid="app-card-sparkline"
            viewBox="0 0 100 22"
            preserveAspectRatio="none"
            className="mt-3 h-6 w-full"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(34,211,238,0.25)" />
                    <stop offset="100%" stopColor="rgba(34,211,238,0)" />
                </linearGradient>
            </defs>
            <polygon points={`0,22 ${pts} 100,22`} fill="url(#spark-fill)" />
            <polyline
                points={pts}
                fill="none"
                stroke="#22d3ee"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ filter: "drop-shadow(0 0 3px rgba(34,211,238,0.55))" }}
            />
        </svg>
    );
};

export default function Dashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [apps, setApps] = useState(null);
    const [selectedId, setSelectedId] = useState(params.get("app") || "all");
    const [detail, setDetail] = useState(null);
    const [newKey, setNewKey] = useState(() => {
        const id = params.get("app");
        const k = id ? sessionStorage.getItem(`mdh-new-key-${id}`) : null;
        return k || null;
    });
    const [copiedKey, setCopiedKey] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [mobileMenu, setMobileMenu] = useState(false);
    const socket = useAppSocket(selectedId !== "all" ? selectedId : null);

    const loadApps = useCallback(async () => {
        try {
            const r = await fetch(`${API}/apps`, { credentials: "include" });
            if (r.status === 401) {
                navigate("/login");
                return;
            }
            const d = await r.json();
            setApps(d.apps || []);
        } catch {
            setApps([]);
        }
    }, [navigate]);

    const loadDetail = useCallback(async (appId) => {
        try {
            const r = await fetch(`${API}/apps/${appId}`, { credentials: "include" });
            if (r.ok) setDetail(await r.json());
        } catch {
            /* keep previous detail */
        }
    }, []);

    useEffect(() => {
        loadApps();
    }, [loadApps]);

    useEffect(() => {
        const id = params.get("app");
        if (id) setSelectedId(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedId !== "all") loadDetail(selectedId);
        else setDetail(null);
        setConfirmDelete(false);
    }, [selectedId, loadDetail]);

    const select = (id) => {
        setSelectedId(id);
        setNewKey(id !== "all" ? sessionStorage.getItem(`mdh-new-key-${id}`) : null);
        setParams(id === "all" ? {} : { app: id }, { replace: true });
    };

    const copyKey = async () => {
        try {
            await navigator.clipboard.writeText(newKey);
            setCopiedKey(true);
            toast.success("Key copied");
            setTimeout(() => setCopiedKey(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    const deleteApp = async () => {
        try {
            const r = await fetch(`${API}/apps/${selectedId}`, {
                method: "DELETE",
                credentials: "include",
            });
            if (!r.ok) throw new Error("failed");
            toast.success("API removed", {
                description: "Its keys, events, and configuration were deleted.",
            });
            setConfirmDelete(false);
            await loadApps();
            select("all");
        } catch {
            toast.error("Could not remove the API");
        }
    };

    const selectedApp = apps?.find((a) => a.app_id === selectedId);
    const stats = detail?.stats || selectedApp?.stats;
    const isMachine = selectedApp?.template === "local-machine-monitor";

    const totals = (apps || []).reduce(
        (acc, a) => ({
            events: acc.events + (a.stats?.events_today || 0),
            errors: acc.errors + (a.stats?.errors || 0),
        }),
        { events: 0, errors: 0 },
    );

    // file-manager-style grouping for the browse view
    const categoryOf = (t) =>
        t === "webhook-receiver" || t === "outbound-webhooks" ? "Webhooks" : t ? "Monitors" : "Custom";
    const groups = ["Webhooks", "Monitors", "Custom"]
        .map((label) => {
            const list = (apps || []).filter((a) => categoryOf(a.template) === label);
            return {
                label,
                list,
                events: list.reduce((n, a) => n + (a.stats?.events_today || 0), 0),
                errors: list.reduce((n, a) => n + (a.stats?.errors || 0), 0),
            };
        })
        .filter((g) => g.list.length > 0);

    return (
        <div data-testid="dashboard-page" className="min-h-screen">
            {/* universal top bar — no sidebar; apps are browsed as cards */}
            <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-[#070B14]/90 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 lg:px-8">
                    <Link to="/dashboard" data-testid="dashboard-logo" className="shrink-0">
                        <img src="/assets/logo-full.png" alt="MDH-API" className="hidden h-5 w-auto sm:block" />
                        <img src="/assets/logo-icon.png" alt="MDH" className="h-6 w-6 sm:hidden" />
                    </Link>
                    {selectedId !== "all" && (
                        <button
                            data-testid="back-to-apps"
                            onClick={() => select("all")}
                            className="hidden items-center gap-1.5 rounded-md border border-slate-800 px-3 py-1.5 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300 sm:flex"
                        >
                            ← All apps
                        </button>
                    )}
                    {apps?.length > 0 && (
                        <select
                            data-testid="mobile-app-switcher"
                            value={selectedId}
                            onChange={(e) => select(e.target.value)}
                            className="min-w-0 flex-1 rounded-md border border-slate-800 bg-[#0D131f] px-3 py-2 font-mono text-xs text-slate-200 focus:border-cyan-500/60 focus:outline-none sm:hidden"
                        >
                            <option value="all">All apps</option>
                            {apps.map((a) => (
                                <option key={a.app_id} value={a.app_id}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                    )}
                    <div className="ml-auto hidden items-center gap-1 md:flex">
                        <Link
                            to="/onboarding"
                            data-testid="topnav-new-app"
                            className="mr-1 flex items-center gap-1.5 rounded-md bg-cyan-400 px-3.5 py-2 font-mono text-[11px] font-bold text-slate-950 transition-colors hover:bg-cyan-300"
                        >
                            <Plus className="h-3.5 w-3.5" /> New App
                        </Link>
                        <Link
                            to="/templates"
                            data-testid="topnav-templates"
                            className="rounded-md px-3 py-2 font-mono text-[11px] text-slate-400 transition-colors hover:bg-slate-900 hover:text-cyan-300"
                        >
                            Templates
                        </Link>
                        <Link
                            to="/settings"
                            data-testid="topnav-settings"
                            className="rounded-md px-3 py-2 font-mono text-[11px] text-slate-400 transition-colors hover:bg-slate-900 hover:text-cyan-300"
                        >
                            Settings
                        </Link>
                        <span className="mx-1 h-4 w-px bg-slate-800" />
                        <span className="max-w-36 truncate px-1 font-mono text-[11px] text-slate-500">
                            {user?.name || user?.email}
                        </span>
                        <button
                            data-testid="topnav-logout"
                            onClick={async () => {
                                await logout();
                                navigate("/");
                            }}
                            className="rounded-md px-2.5 py-2 font-mono text-[11px] text-slate-500 transition-colors hover:bg-slate-900 hover:text-rose-300"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    <div className="relative ml-auto shrink-0 md:hidden">
                        <button
                            data-testid="mobile-menu-button"
                            onClick={() => setMobileMenu((v) => !v)}
                            aria-label="Menu"
                            className="rounded-md border border-slate-800 p-2 text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-slate-200"
                        >
                            {mobileMenu ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                        </button>
                        {mobileMenu && (
                            <>
                                <button
                                    aria-hidden="true"
                                    tabIndex={-1}
                                    className="fixed inset-0 z-10 cursor-default"
                                    onClick={() => setMobileMenu(false)}
                                />
                                <div
                                    data-testid="mobile-menu"
                                    className="glass-panel absolute right-0 top-full z-20 mt-2 w-52 rounded-lg p-1.5 shadow-xl"
                                >
                                    <div className="truncate px-3 py-2 font-mono text-[10px] text-slate-500">
                                        {user?.email}
                                    </div>
                                    <Link
                                        to="/onboarding"
                                        data-testid="mobile-menu-new-app"
                                        onClick={() => setMobileMenu(false)}
                                        className="flex items-center gap-2.5 rounded-md px-3 py-2 font-mono text-[11px] text-slate-400 transition-colors hover:bg-slate-800/70 hover:text-cyan-300"
                                    >
                                        <Plus className="h-3.5 w-3.5" /> New App
                                    </Link>
                                    <Link
                                        to="/templates"
                                        data-testid="mobile-menu-templates"
                                        onClick={() => setMobileMenu(false)}
                                        className="flex items-center gap-2.5 rounded-md px-3 py-2 font-mono text-[11px] text-slate-400 transition-colors hover:bg-slate-800/70 hover:text-cyan-300"
                                    >
                                        <Boxes className="h-3.5 w-3.5" /> Templates
                                    </Link>
                                    <Link
                                        to="/settings"
                                        data-testid="mobile-menu-settings"
                                        onClick={() => setMobileMenu(false)}
                                        className="flex items-center gap-2.5 rounded-md px-3 py-2 font-mono text-[11px] text-slate-400 transition-colors hover:bg-slate-800/70 hover:text-cyan-300"
                                    >
                                        <SettingsIcon className="h-3.5 w-3.5" /> Settings
                                    </Link>
                                    <button
                                        data-testid="mobile-menu-logout"
                                        onClick={async () => {
                                            setMobileMenu(false);
                                            await logout();
                                            navigate("/");
                                        }}
                                        className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 font-mono text-[11px] text-slate-400 transition-colors hover:bg-slate-800/70 hover:text-rose-300"
                                    >
                                        <LogOut className="h-3.5 w-3.5" /> Log out
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <main className="mx-auto w-full max-w-7xl px-4 py-6 lg:px-8 lg:py-8">
                {apps === null ? (
                    <p className="font-mono text-xs text-slate-500">Loading your apps…</p>
                ) : apps.length === 0 ? (
                    <div className="flex min-h-[70vh] flex-col items-center justify-center text-center">
                        <Sparkles className="h-8 w-8 text-cyan-500/60" />
                        <h1 className="font-hero mt-5 text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
                            No APIs yet.
                        </h1>
                        <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                            Describe what you're building in one sentence — MDH scaffolds the API,
                            keys, and dashboard.
                        </p>
                        <Link
                            to="/onboarding"
                            data-testid="dashboard-empty-cta"
                            className="mt-7 rounded-md bg-cyan-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-[0_0_24px_rgba(6,182,212,0.4)] transition-colors hover:bg-cyan-300"
                        >
                            Create your first app
                        </Link>
                    </div>
                ) : selectedId === "all" ? (
                    <div>
                        <h1 className="font-display text-xl font-bold tracking-tight text-slate-100">
                            Your apps
                        </h1>
                        <p className="mt-1 font-mono text-[11px] text-slate-500">
                            All apps · today · click a card to open it
                        </p>
                        <div className="mt-6 grid grid-cols-2 gap-3 xl:grid-cols-4">
                            <StatCard testid="stat-total-apps" label="Apps" value={apps.length} />
                            <StatCard testid="stat-total-events" label="Events today" value={totals.events} />
                            <StatCard testid="stat-total-errors" label="Errors today" value={totals.errors} />
                            <StatCard
                                testid="stat-plan-usage"
                                label="Plan usage"
                                value={`${Math.min(100, Math.round((totals.events / 10000) * 100))}%`}
                                sub="Developer plan · 10k/mo"
                            />
                        </div>

                        {groups.map((g) => (
                            <section key={g.label} data-testid={`browse-group-${g.label.toLowerCase()}`} className="mt-8">
                                <div className="flex items-baseline justify-between gap-3">
                                    <h2 className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                        {g.label}
                                    </h2>
                                    <span className="font-mono text-[10px] text-slate-600">
                                        {g.list.length} app{g.list.length === 1 ? "" : "s"} · {g.events} events today
                                        {g.errors > 0 && (
                                            <span className="text-amber-400"> · {g.errors} errors</span>
                                        )}
                                    </span>
                                </div>
                                <div className="mt-3 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                    {g.list.map((a) => {
                                        const Icon = TEMPLATE_ICONS[a.template] || Boxes;
                                        const errs = a.stats?.errors || 0;
                                        return (
                                            <button
                                                key={a.app_id}
                                                data-testid={`app-card-${a.app_id}`}
                                                onClick={() => select(a.app_id)}
                                                className="group rounded-xl border border-slate-800/80 bg-[#111827] p-5 text-left transition-all duration-300 hover:border-cyan-500/40 hover:shadow-[0_0_24px_rgba(6,182,212,0.10)]"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-500/20 bg-cyan-500/10">
                                                        <Icon className="h-4 w-4 text-cyan-300" />
                                                    </span>
                                                    <StatusDot tone={errs > 0 ? "amber" : "emerald"} />
                                                </div>
                                                <div className="mt-3.5 truncate font-display text-sm font-bold tracking-tight text-slate-100">
                                                    {a.name}
                                                </div>
                                                <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">
                                                    {a.template || "custom"}
                                                </div>
                                                <Sparkline data={a.stats?.spark} />
                                                <div className="mt-3 flex items-center gap-4 border-t border-slate-800/70 pt-3 font-mono text-[10px] text-slate-500">
                                                    <span>{a.stats?.events_today ?? 0} events</span>
                                                    <span className={errs > 0 ? "text-amber-300" : ""}>
                                                        {errs} errors
                                                    </span>
                                                    <span className="ml-auto text-emerald-300">
                                                        {a.stats?.delivery_rate ?? 100}%
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>
                        ))}
                    </div>
                ) : (
                    <div>
                        <div className="flex flex-wrap items-center gap-3">
                            <h1
                                data-testid="dashboard-app-name"
                                className="font-display text-xl font-bold tracking-tight text-slate-100"
                            >
                                {selectedApp?.name || "App"}
                            </h1>
                            {selectedApp?.template && (
                                <span className="rounded border border-teal-500/30 bg-teal-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-teal-300">
                                    {selectedApp.template}
                                </span>
                            )}
                            <span className="flex items-center gap-1.5 font-mono text-[10px] text-slate-500">
                                <StatusDot /> active
                            </span>
                            {!confirmDelete ? (
                                <button
                                    data-testid="app-delete-button"
                                    onClick={() => setConfirmDelete(true)}
                                    className="ml-auto flex items-center gap-1.5 rounded-md border border-slate-800 px-2.5 py-1.5 font-mono text-[10px] text-slate-500 transition-colors hover:border-rose-500/40 hover:text-rose-300"
                                >
                                    <Trash2 className="h-3 w-3" /> Remove
                                </button>
                            ) : (
                                <span className="ml-auto flex items-center gap-2">
                                    <span className="font-mono text-[10px] text-rose-300">
                                        Delete this API and all its data?
                                    </span>
                                    <button
                                        data-testid="app-delete-confirm"
                                        onClick={deleteApp}
                                        className="rounded-md border border-rose-500/40 bg-rose-950/40 px-2.5 py-1.5 font-mono text-[10px] font-semibold text-rose-300 transition-colors hover:bg-rose-950/70"
                                    >
                                        confirm delete
                                    </button>
                                    <button
                                        data-testid="app-delete-cancel"
                                        onClick={() => setConfirmDelete(false)}
                                        className="rounded-md border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-400 transition-colors hover:text-slate-200"
                                    >
                                        cancel
                                    </button>
                                </span>
                            )}
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3 xl:grid-cols-4">
                            <StatCard testid="stat-events-today" label="Events today" value={stats?.events_today ?? 0} />
                            <StatCard
                                testid="stat-delivery-rate"
                                label="Delivery rate"
                                value={`${stats?.delivery_rate ?? 100}%`}
                            />
                            <StatCard testid="stat-errors" label="Errors" value={stats?.errors ?? 0} />
                            <StatCard testid="stat-p99" label="P99 latency" value={`${stats?.p99 ?? 0}ms`} />
                        </div>

                        {newKey && (
                            <div
                                data-testid="new-key-banner"
                                className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyan-500/30 bg-cyan-950/30 px-5 py-4"
                            >
                                <div className="min-w-0">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">
                                        New API key — shown once
                                    </div>
                                    <code className="mt-1 block truncate font-mono text-xs text-cyan-200">
                                        {newKey}
                                    </code>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        data-testid="new-key-copy"
                                        onClick={copyKey}
                                        className="flex items-center gap-1.5 rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-300 hover:border-cyan-500/40"
                                    >
                                        {copiedKey ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                                        {copiedKey ? "copied" : "copy"}
                                    </button>
                                    <button
                                        data-testid="new-key-dismiss"
                                        onClick={() => {
                                            setNewKey(null);
                                            sessionStorage.removeItem(`mdh-new-key-${selectedId}`);
                                        }}
                                        className="rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-500 hover:text-slate-300"
                                    >
                                        dismiss
                                    </button>
                                </div>
                            </div>
                        )}

                        {isMachine && (
                            <div className="glass-panel mt-6 rounded-xl p-5">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                                        <Terminal className="h-4 w-4 text-cyan-400" />
                                        Connect a machine
                                    </h3>
                                    {(() => {
                                        const liveAgents = (socket.agents || []).filter((a) => a.live);
                                        const seen = (socket.agents || [])
                                            .map((a) => a.last_seen)
                                            .filter(Boolean)
                                            .sort()
                                            .pop();
                                        const pill = liveAgents.length
                                            ? [
                                                  `agent connected · ${liveAgents.map((a) => a.name || a.host_id).join(", ")}`,
                                                  "border-emerald-500/30 bg-emerald-950/40 text-emerald-300",
                                              ]
                                            : (socket.agents || []).length
                                              ? [
                                                    `agent offline · last seen ${seen ? seen.slice(11, 19) : "—"} UTC`,
                                                    "border-rose-500/30 bg-rose-950/40 text-rose-300",
                                                ]
                                              : [
                                                    "waiting for agent…",
                                                    "border-slate-700 bg-slate-900 text-slate-400",
                                                ];
                                        return (
                                            <span
                                                data-testid="agent-status-badge"
                                                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest ${pill[1]}`}
                                            >
                                                <span
                                                    className={`h-1.5 w-1.5 rounded-full ${
                                                        liveAgents.length ? "bg-emerald-400 animate-pulse-dot" : "bg-slate-600"
                                                    }`}
                                                />
                                                {pill[0]}
                                            </span>
                                        );
                                    })()}
                                </div>
                                <ol className="mt-4 space-y-3 font-mono text-[11px] leading-relaxed text-slate-400">
                                    <li>
                                        <span className="text-slate-600">1 ·</span> Install deps:{" "}
                                        <code className="rounded bg-slate-900 px-1.5 py-0.5 text-cyan-300">
                                            pip install psutil requests
                                        </code>
                                    </li>
                                    <li className="flex flex-wrap items-center gap-2">
                                        <span className="text-slate-600">2 ·</span> Download the agent:{" "}
                                        <a
                                            data-testid="agent-download-link"
                                            href={`${API}/agent/script`}
                                            className="rounded border border-cyan-500/40 bg-cyan-950/40 px-2.5 py-1 text-cyan-300 hover:bg-cyan-950/70"
                                        >
                                            mdh_agent.py
                                        </a>
                                    </li>
                                    <li className="break-all">
                                        <span className="text-slate-600">3 ·</span> Run it:{" "}
                                        <code className="rounded bg-slate-900 px-1.5 py-0.5 text-cyan-300">
                                            python mdh_agent.py --api {BACKEND} --key &lt;publish key&gt;
                                        </code>
                                    </li>
                                </ol>
                                <div className="mt-4">
                                    <button
                                        data-testid="generate-agent-key-button"
                                        onClick={async () => {
                                            try {
                                                const r = await fetch(`${API}/apps/${selectedId}/keys`, {
                                                    method: "POST",
                                                    credentials: "include",
                                                    headers: { "Content-Type": "application/json" },
                                                    body: JSON.stringify({ label: "agent key", scope: "publish" }),
                                                });
                                                const d = await r.json();
                                                setNewKey(d.key);
                                                sessionStorage.setItem(`mdh-new-key-${selectedId}`, d.key);
                                                loadDetail(selectedId);
                                            } catch {
                                                toast.error("Could not create key");
                                            }
                                        }}
                                        className="rounded-md border border-cyan-500/40 bg-cyan-950/40 px-4 py-2 font-mono text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70"
                                    >
                                        Generate agent key
                                    </button>
                                </div>
                            </div>
                        )}

                        <FlowVisualizer
                            app={detail?.app || selectedApp}
                            events={socket.events}
                            live={socket.live}
                        />

                        <div className="mt-6">
                            <LiveStream events={socket.events} live={socket.live} appId={selectedId} />
                        </div>

                        {!selectedApp?.template && (
                            <DevelopPanel
                                appId={selectedId}
                                app={detail?.app || selectedApp}
                                onChanged={() => {
                                    loadApps();
                                    loadDetail(selectedId);
                                }}
                            />
                        )}

                        {selectedApp?.template === "rest-api-monitor" && (
                            <div className="mt-4">
                                <UptimePanel
                                    appId={selectedId}
                                    app={detail?.app || selectedApp}
                                    checks={socket.uptime}
                                    onSaved={() => {
                                        loadApps();
                                        loadDetail(selectedId);
                                    }}
                                />
                            </div>
                        )}

                        {selectedApp?.template === "webhook-receiver" && (
                            <div className="mt-4">
                                <ReceiverPanel
                                    appId={selectedId}
                                    app={detail?.app || selectedApp}
                                    hooks={socket.hooks}
                                    onConfigSaved={() => loadDetail(selectedId)}
                                />
                            </div>
                        )}

                        {selectedApp?.template === "node-project-monitor" && (
                            <div className="mt-4">
                                <NodePanel appId={selectedId} nodestats={socket.nodestats} />
                            </div>
                        )}

                        {selectedApp?.template === "outbound-webhooks" && (
                            <div className="mt-4">
                                <OutboundPanel
                                    appId={selectedId}
                                    deliveries={socket.deliveries}
                                    clearStamp={socket.deliveryClearStamp}
                                    eventTypes={[...new Set(socket.events.map((e) => e.type).filter(Boolean))].slice(0, 12)}
                                />
                            </div>
                        )}

                        {selectedApp?.template === "cron-monitor" && (
                            <div className="mt-4">
                                <CronPanel
                                    appId={selectedId}
                                    app={detail?.app || selectedApp}
                                    socketJobs={socket.cronjobs}
                                />
                            </div>
                        )}

                        {selectedApp?.template === "ssl-cert-monitor" && (
                            <div className="mt-4">
                                <SslPanel
                                    appId={selectedId}
                                    app={detail?.app || selectedApp}
                                    socketCheck={socket.sslcheck}
                                />
                            </div>
                        )}

                        {(isMachine || selectedApp?.template === "docker-monitor") && (
                            <div className="mt-4">
                                <ChildrenGrid appId={selectedId} metrics={socket.metrics} />
                            </div>
                        )}

                        <div className="mt-4">
                            <CurlPanel appId={selectedId} app={detail?.app || selectedApp} />
                        </div>

                        <div className="mt-4">
                            <ConsolePanel
                                appId={selectedId}
                                events={socket.events}
                                hooks={socket.hooks}
                                live={socket.live}
                            />
                        </div>

                        <div className="mt-4">
                            <ApiReference appId={selectedId} />
                        </div>

                        <KeysPanel
                            appId={selectedId}
                            keys={detail?.keys || []}
                            onNewKey={(k) => {
                                setNewKey(k);
                                sessionStorage.setItem(`mdh-new-key-${selectedId}`, k);
                            }}
                            onChanged={() => loadDetail(selectedId)}
                        />
                    </div>
                )}
            </main>
        </div>
    );
}
